<template>
  <div class="test-page">
    <!-- 白名单拦截提示 -->
    <div v-if="blocked" class="blocked-wrap">
      <div class="blocked-icon">🚫</div>
      <div class="blocked-title">访问受限</div>
      <div class="blocked-desc">当前用户不在测试白名单中</div>
      <div class="blocked-openid">OpenID：{{ currentOpenId || '未获取' }}</div>
    </div>

    <!-- 测试支付页面主体 -->
    <div v-else>
      <div class="test-header">
        <div class="test-badge">TEST</div>
        <div class="test-title">微信支付参数调试</div>
      </div>

      <div class="card">
        <div class="card-title">当前用户</div>
        <div class="info-row">
          <span class="info-label">OpenID</span>
          <span class="info-value small">{{ currentOpenId || '未获取（非微信环境）' }}</span>
        </div>
        <div class="info-row">
          <span class="info-label">环境</span>
          <span class="info-value" :class="inWechat ? 'green' : 'orange'">{{ inWechat ? '微信内置浏览器' : '普通浏览器' }}</span>
        </div>
      </div>

      <div class="card">
        <div class="card-title">支付参数</div>
        <div class="info-row" v-for="(val, key) in payParams" :key="key">
          <span class="info-label">{{ key }}</span>
          <span class="info-value small breakable">{{ val }}</span>
        </div>
      </div>

      <div class="card">
        <div class="card-title">白名单管理</div>
        <div class="whitelist-wrap">
          <div v-for="(item, idx) in whitelist" :key="idx" class="whitelist-item">
            <span class="wl-text">{{ item }}</span>
            <span class="wl-remove" @click="removeWhitelist(idx)">✕</span>
          </div>
          <div v-if="whitelist.length === 0" class="wl-empty">白名单为空（所有人可访问）</div>
        </div>
        <div class="wl-add-row">
          <input
            class="wl-input"
            v-model="newOpenId"
            placeholder="输入 OpenID 添加到白名单"
            @keyup.enter="addWhitelist"
          />
          <button class="wl-btn" @click="addWhitelist">添加</button>
          <button class="wl-btn wl-btn-self" @click="addSelf" v-if="currentOpenId">添加自己</button>
        </div>
      </div>

      <div class="pay-btn-wrap">
        <button class="pay-btn" @click="triggerPay" :disabled="paying">
          {{ paying ? '支付中...' : '立即发起支付' }}
        </button>
      </div>

      <div v-if="resultMsg" class="result-box" :class="resultSuccess ? 'success' : 'fail'">
        {{ resultMsg }}
      </div>

      <div class="log-card" v-if="logs.length > 0">
        <div class="card-title">调试日志</div>
        <div v-for="(log, i) in logs" :key="i" class="log-item">{{ log }}</div>
      </div>
    </div>
  </div>
</template>

<script>
const WHITELIST_KEY = 'wxpay_test_whitelist';

export default {
  name: 'WxpayTest',
  data() {
    return {
      payParams: {
        appId: 'wx1ca4cb02bb26bcc4',
        timeStamp: '1780551122',
        nonceStr: '776e330ef8d647ae9cee371b103b3d49',
        package: 'prepay_id=wx0413320200852460b2ae36d983cb560001',
        signType: 'RSA',
        paySign: 'zVtuyLAApK4S0yLlMQ9YifonZtEan8O7teNketWJOniaXJLdEAMgp1WXERue6zeLFpn6bOnMkQ1fNv7kRYTw549ZePHCL+OXkEWJzvJsv18Yc0JjN52zNk5ZSasDr4jFkWjFE9eUSecuA1uWBfTtErig4W7EDI/b9pvkwHb5UlpxmfadA0L7OGYC5Dp6/bKNPgaZRAh59pwQJQToHYQwuG28buqm/UXjggFOM1hNiAZ3uAJsHbkFC/BbWyOrkIoaEvw71cK3e+PyFZlIK16ETdFUx8yvdYQea6CD7znxBB1HM1nQ0UJbS26ID9BqgMOVUZ7Mkgpfu1MBznIseZ2q8A=='
      },
      whitelist: [],
      newOpenId: '',
      currentOpenId: '',
      paying: false,
      resultMsg: '',
      resultSuccess: false,
      logs: [],
      blocked: false
    };
  },
  computed: {
    inWechat() {
      return /MicroMessenger/i.test(navigator.userAgent);
    }
  },
  mounted() {
    this.loadWhitelist();
    this.resolveOpenId();
  },
  methods: {
    addLog(msg) {
      const now = new Date();
      const ts = `${now.getHours().toString().padStart(2,'0')}:${now.getMinutes().toString().padStart(2,'0')}:${now.getSeconds().toString().padStart(2,'0')}`;
      this.logs.unshift(`[${ts}] ${msg}`);
    },
    loadWhitelist() {
      try {
        const saved = localStorage.getItem(WHITELIST_KEY);
        this.whitelist = saved ? JSON.parse(saved) : [];
      } catch {
        this.whitelist = [];
      }
    },
    saveWhitelist() {
      localStorage.setItem(WHITELIST_KEY, JSON.stringify(this.whitelist));
    },
    addWhitelist() {
      const id = this.newOpenId.trim();
      if (!id) return;
      if (!this.whitelist.includes(id)) {
        this.whitelist.push(id);
        this.saveWhitelist();
        this.addLog(`已添加白名单：${id}`);
      }
      this.newOpenId = '';
    },
    addSelf() {
      if (this.currentOpenId && !this.whitelist.includes(this.currentOpenId)) {
        this.whitelist.push(this.currentOpenId);
        this.saveWhitelist();
        this.addLog(`已将自己添加到白名单：${this.currentOpenId}`);
      }
    },
    removeWhitelist(idx) {
      const removed = this.whitelist.splice(idx, 1);
      this.saveWhitelist();
      this.addLog(`已移除白名单：${removed[0]}`);
      this.checkBlocked();
    },
    resolveOpenId() {
      // 从 URL query 中读取 openid（由后端 OAuth 流程注入）
      const openid = this.$route.query.openid || sessionStorage.getItem('test_openid') || '';
      this.currentOpenId = openid;
      if (openid) sessionStorage.setItem('test_openid', openid);
      this.addLog(`OpenID：${openid || '未获取'}`);
      this.checkBlocked();
    },
    checkBlocked() {
      if (this.whitelist.length === 0) {
        this.blocked = false;
        return;
      }
      this.blocked = !!this.currentOpenId && !this.whitelist.includes(this.currentOpenId);
    },
    triggerPay() {
      this.resultMsg = '';
      this.paying = true;
      this.addLog('开始调用 WeixinJSBridge...');

      if (typeof WeixinJSBridge === 'undefined') {
        this.addLog('WeixinJSBridge 未定义，监听 WeixinJSBridgeReady 事件');
        if (document.addEventListener) {
          document.addEventListener('WeixinJSBridgeReady', this.onBridgeReady, false);
        } else if (document.attachEvent) {
          document.attachEvent('WeixinJSBridgeReady', this.onBridgeReady);
          document.attachEvent('onWeixinJSBridgeReady', this.onBridgeReady);
        }
        setTimeout(() => {
          if (this.paying) {
            this.paying = false;
            this.resultMsg = '等待超时：请在微信内置浏览器中打开本页面';
            this.resultSuccess = false;
            this.addLog('超时：WeixinJSBridge 未就绪');
          }
        }, 5000);
      } else {
        this.onBridgeReady();
      }
    },
    onBridgeReady() {
      this.addLog('WeixinJSBridge 就绪，发起 getBrandWCPayRequest');
      const that = this;
      // eslint-disable-next-line no-undef
      WeixinJSBridge.invoke('getBrandWCPayRequest', { ...this.payParams }, function(res) {
        that.paying = false;
        that.addLog(`支付回调：${JSON.stringify(res)}`);
        if (res.err_msg === 'get_brand_wcpay_request:ok') {
          that.resultMsg = '支付成功！';
          that.resultSuccess = true;
        } else if (res.err_msg === 'get_brand_wcpay_request:cancel') {
          that.resultMsg = '用户取消支付';
          that.resultSuccess = false;
        } else {
          that.resultMsg = `支付失败：${res.err_msg || JSON.stringify(res)}`;
          that.resultSuccess = false;
        }
      });
    }
  }
};
</script>

<style scoped>
.test-page {
  min-height: 100vh;
  background: #f5f5f5;
  padding: 0 0 40px;
  font-size: 14px;
  color: #333;
}

.test-header {
  background: linear-gradient(135deg, #07c160, #06ad56);
  padding: 24px 16px 16px;
  display: flex;
  align-items: center;
  gap: 10px;
}

.test-badge {
  background: #fff;
  color: #07c160;
  font-size: 11px;
  font-weight: bold;
  padding: 2px 7px;
  border-radius: 4px;
  letter-spacing: 1px;
}

.test-title {
  color: #fff;
  font-size: 17px;
  font-weight: 600;
}

.card {
  background: #fff;
  margin: 12px 12px 0;
  border-radius: 10px;
  padding: 14px 16px;
  box-shadow: 0 1px 4px rgba(0,0,0,.06);
}

.card-title {
  font-size: 13px;
  color: #999;
  margin-bottom: 10px;
  font-weight: 500;
  letter-spacing: .5px;
}

.info-row {
  display: flex;
  align-items: flex-start;
  padding: 5px 0;
  border-bottom: 1px solid #f5f5f5;
}

.info-row:last-child { border-bottom: none; }

.info-label {
  min-width: 80px;
  color: #666;
  font-size: 13px;
  padding-top: 1px;
}

.info-value {
  flex: 1;
  font-size: 13px;
  word-break: break-all;
  color: #333;
}

.info-value.small { font-size: 12px; color: #555; }
.info-value.breakable { word-break: break-all; }
.info-value.green { color: #07c160; }
.info-value.orange { color: #fa8c16; }

.whitelist-wrap { margin-bottom: 10px; }

.whitelist-item {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 6px 0;
  border-bottom: 1px solid #f0f0f0;
}

.wl-text { font-size: 12px; color: #444; word-break: break-all; flex: 1; }
.wl-remove { color: #ff4d4f; cursor: pointer; padding: 0 4px; font-size: 14px; margin-left: 8px; }
.wl-empty { font-size: 12px; color: #bbb; padding: 6px 0; }

.wl-add-row {
  display: flex;
  gap: 8px;
  margin-top: 10px;
  align-items: center;
}

.wl-input {
  flex: 1;
  border: 1px solid #ddd;
  border-radius: 6px;
  padding: 7px 10px;
  font-size: 12px;
  outline: none;
}

.wl-input:focus { border-color: #07c160; }

.wl-btn {
  background: #07c160;
  color: #fff;
  border: none;
  border-radius: 6px;
  padding: 7px 14px;
  font-size: 12px;
  cursor: pointer;
  white-space: nowrap;
}

.wl-btn-self { background: #1890ff; }

.pay-btn-wrap {
  margin: 20px 12px 0;
}

.pay-btn {
  width: 100%;
  background: #07c160;
  color: #fff;
  border: none;
  border-radius: 10px;
  padding: 14px 0;
  font-size: 16px;
  font-weight: 600;
  cursor: pointer;
  letter-spacing: 1px;
}

.pay-btn:disabled { background: #95d5b2; cursor: not-allowed; }

.result-box {
  margin: 12px 12px 0;
  padding: 12px 16px;
  border-radius: 8px;
  font-size: 14px;
  font-weight: 500;
  text-align: center;
}

.result-box.success { background: #f6ffed; color: #52c41a; border: 1px solid #b7eb8f; }
.result-box.fail { background: #fff2f0; color: #ff4d4f; border: 1px solid #ffa39e; }

.log-card {
  background: #1e1e1e;
  margin: 12px 12px 0;
  border-radius: 10px;
  padding: 14px 16px;
}

.log-card .card-title { color: #888; }

.log-item {
  font-size: 11px;
  color: #a8ff78;
  font-family: 'Courier New', monospace;
  padding: 2px 0;
  border-bottom: 1px solid #2a2a2a;
}

.blocked-wrap {
  min-height: 100vh;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  text-align: center;
  padding: 32px;
}

.blocked-icon { font-size: 60px; margin-bottom: 16px; }
.blocked-title { font-size: 20px; font-weight: bold; color: #333; margin-bottom: 8px; }
.blocked-desc { font-size: 14px; color: #999; margin-bottom: 12px; }
.blocked-openid { font-size: 12px; color: #bbb; word-break: break-all; }
</style>
